"use client";

import { DocumentItem } from "@/components/app/documents/document-item";
import { DocumentSelectorDialog } from "@/components/app/documents/document-selector-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import { uploadFile } from "@/lib/storage";
import type {
  Document,
  DocumentCategory,
  DocumentType,
  uploadSchema,
} from "@/schemas/document";
import { FileText } from "lucide-react";
import { useEffect, useState } from "react";
import type { z } from "zod";

interface RequiredDocument {
  id: string;
  title: string;
  description: string;
  required: boolean;
  category: DocumentCategory;
  type: DocumentType;
  expectedFrom?: string;
}

const PRE_APPROVAL_DOC: RequiredDocument = {
  id: "pre_approval",
  title: "Pre-approval Letter",
  description:
    "Official letter from your lender confirming your pre-approval status and amount",
  required: true,
  category: "mortgage",
  type: "pre_approval",
  expectedFrom: "Lender",
};

export function PreApprovalUploader() {
  const {
    createDocument,
    addBuyerDocument,
    deleteBuyerDocument,
    updateBuyerDocument,
    journeyData,
    journeyDocuments,
    documents,
    userProfile,
  } = useJourneyData();
  const [documentSelections, setDocumentSelections] = useState<
    Record<string, Document>
  >({});
  const [activeDocumentSelection, setActiveDocumentSelection] =
    useState<RequiredDocument | null>(null);
  const { toast } = useToast();

  if (!journeyData?.id) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto space-y-6">
          <div className="bg-primary/5 rounded-2xl p-8 mx-auto">
            <div className="bg-primary/10 rounded-full p-5 w-20 h-20 mx-auto flex items-center justify-center">
              <FileText className="w-10 h-10 text-primary" />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              No Active Journey
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              You need to create or select a journey before you can manage
              documents.
            </p>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!journeyDocuments?.length || !documents?.length) return;

    const referenceId = `${PRE_APPROVAL_DOC.category}-${PRE_APPROVAL_DOC.type}`;
    const journeyDoc = journeyDocuments.find(
      (bd) => bd.reference_id === referenceId,
    );

    if (journeyDoc) {
      const doc = documents.find((d) => d.id === journeyDoc.document.id);
      if (doc) {
        setDocumentSelections({ [PRE_APPROVAL_DOC.id]: doc });
      }
    }
  }, [journeyDocuments, documents]);

  const handleDocumentSelect = async (doc: Document | null) => {
    if (!doc || !activeDocumentSelection || !userProfile?.id) return;

    try {
      const referenceId = `${activeDocumentSelection.category}-${activeDocumentSelection.type}`;
      const existingJourneyDoc = journeyDocuments.find(
        (bd) => bd.reference_id === referenceId,
      );

      // Close the dialog
      setActiveDocumentSelection(null);

      if (existingJourneyDoc) {
        await updateBuyerDocument.mutateAsync({
          id: existingJourneyDoc.id,
          document_id: doc.id,
        });
      } else {
        await addBuyerDocument.mutateAsync({
          document_id: doc.id,
          journey_id: journeyData?.id ?? "",
          user_id: userProfile.id,
          reference_id: referenceId,
        });
      }
    } catch (error) {
      console.error("Failed to update document selection:", error);
      toast({
        title: "Error",
        description: "Failed to update document selection",
        variant: "destructive",
      });
    }
  };

  const handleUpload = async (formData: z.infer<typeof uploadSchema>) => {
    if (!userProfile?.id) throw new Error("No buyer ID found");

    try {
      const fileName = `${userProfile.id}/mortgage/pre_approval/${Date.now()}_${formData.file.name}`;
      const { url, path } = await uploadFile(
        "documents",
        fileName,
        formData.file,
      );

      const newDocument: Document | null = await createDocument.mutateAsync({
        type: formData.type,
        category: formData.category,
        name: formData.file.name,
        url,
        file_type: formData.file.type,
        file_path: path,
        size_bytes: formData.file.size,
      });
      if (newDocument) {
        handleDocumentSelect(newDocument);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload Failed",
        description:
          error instanceof Error ? error.message : "Failed to upload file",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDelete = async () => {
    const referenceId = `${PRE_APPROVAL_DOC.category}-${PRE_APPROVAL_DOC.type}`;
    const journeyDoc = journeyDocuments.find(
      (bd) => bd.reference_id === referenceId,
    );

    if (journeyDoc) {
      try {
        await deleteBuyerDocument.mutateAsync(journeyDoc.id);
        setDocumentSelections({});
      } catch (error) {
        console.error("Failed to delete document:", error);
        toast({
          title: "Error",
          description: "Failed to delete document",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <>
      <DocumentItem
        doc={PRE_APPROVAL_DOC}
        selectedDocument={documentSelections[PRE_APPROVAL_DOC.id]}
        onSelect={() => setActiveDocumentSelection(PRE_APPROVAL_DOC)}
        onDelete={handleDelete}
      />

      {activeDocumentSelection && (
        <DocumentSelectorDialog
          open={!!activeDocumentSelection}
          onOpenChange={(open) => !open && setActiveDocumentSelection(null)}
          onSelect={handleDocumentSelect}
          documents={documents.filter(
            (doc) =>
              doc.category === activeDocumentSelection.category &&
              doc.type === activeDocumentSelection.type,
          )}
          selectedDocument={documentSelections[activeDocumentSelection.id]}
          category={activeDocumentSelection.category}
          type={activeDocumentSelection.type}
          title={`Add ${activeDocumentSelection.title}`}
          description="Select an existing document or upload a new one"
          onUpload={handleUpload}
          strictType={true}
        />
      )}
    </>
  );
}
